/**
 * Applies a `LayoutResult` from the layout engine (src/layout) to the DOM.
 *
 * Every protocol element is `position: absolute`. The engine reports frames
 * in screen coordinates; an element is positioned relative to the *origin* of
 * its parent element's box, which is the parent's frame except for:
 *
 * - `scrollview`: children live in `.sb-scroll-content` (sized from
 *   `contentSizes`, offset by `chrome.content`) so the browser scrolls it;
 * - `list`: children live in `.sb-list-content`, sized from `contentSizes`;
 * - `section`: rows live in `.sb-section-rows`, the inset card (`chrome.rows`);
 * - `sheet`: the element itself is a full-screen layer (scrim); the engine's
 *   frame is applied to its card and children are relative to the card;
 * - `tabview`: tabs live in `.sb-tabview-content`; the tab bar (`chrome.bar`,
 *   items at `chrome.item<i>`) is drawn on top. Tabs in `result.hidden` get
 *   `data-sb-tab-hidden` (visibility hidden, inert) so their DOM and scroll
 *   positions survive; their subtrees have no frames and are left alone.
 *
 * A list row's DOM box is the full-width row (`chrome.row`) rather than its
 * content frame, so the row background, separator and hit area match iOS;
 * `text`, `image` and `textfield` rows pad their content back into place.
 *
 * Phase 6: the items of a `menu` (every child but the first) are in
 * `result.hidden` without frames; they get the `hidden` attribute so they
 * never show inline (the renderer lists them in a popover). A list row whose
 * `styled` carries a `badge` gets a `.sb-badge` span at `chrome.badge`; an
 * inline `picker` positions its label/option rows; a navscreen's inline title
 * is positioned at `chrome.title` so it truncates around the bar items.
 *
 * Phase 7: the candidates a `viewthatfits` did not choose are in
 * `result.hidden` without frames and get the `hidden` attribute like menu
 * items (and lose it when chosen later); a `navsplit` positions its
 * separator hairline at `chrome.separator`.
 */

import type { LayoutEnvironment, LayoutNode, LayoutResult, Rect, ResolvedFont, TextLayout } from './layout';
import { hostContentInset } from './hostInset';
import { listRowBackgroundOf } from './layout';
import { isRegularWidth } from './layout/engine';
import { styleToCSS } from './styles';
import type { ShapeStyle } from './protocol';
import { applyChartChrome } from './chartDom';
import { bitmapLaidOut } from './bitmaps';
import { drawRichText } from './richText';
import { isRichEditor, layOutRichEditor } from './richEditor';
import { bindScrollUnderBar, largeTitleOf, largeTitleScrolls, largeTitleTextOf } from './largeTitle';
import { bindScrollEdgeBottom } from './scrollEdge';
import { beginScrollSyncBatch, flushScrollSyncBatch, type ScrollExtent } from './scrollSync';
import { BAR_GLASS_KIND, TOOLBAR_ANIMATION, TOOLBAR_TRANSITION, baseTiming, timingFor, transitionStart } from './animation';

export interface LayoutDomContext {
  root: HTMLElement;
  elements: Map<number, HTMLElement>;
  nodes: Map<number, LayoutNode>;
  parents: Map<number, number>;
  /**
   * Boxes applied by the previous layout, by id, relative to the origin of
   * the parent element (the coordinates the inline `left`/`top` use). A child
   * that moves only because its parent moved therefore reads as unchanged:
   * it is neither repositioned nor animated, the parent is.
   */
  boxes: Map<number, Rect>;
  /** Elements whose inline style was reset since the previous layout. */
  dirty: Set<number>;
  /** The environment the result was laid out for (screen size, safe areas). */
  env: LayoutEnvironment;
}

/**
 * An element whose DOM box moved or resized (`from` is null for a new
 * element). Boxes are relative to the parent's origin, so the delta is the
 * element's own motion and not its ancestors'.
 */
export interface BoxChange {
  id: number;
  el: HTMLElement;
  kind: string;
  from: Rect | null;
  to: Rect;
}

const ROOT_ID = 0;
const SWITCH_SIZE = { width: 51, height: 31 };
const CHEVRON_SIZE = 14;
const NAV_BAR_HEIGHT = 44;

/**
 * The changes of the layout being applied, for `placeChrome` to report the
 * chrome it moves (null outside `applyLayout`).
 */
let chromeChanges: BoxChange[] | null = null;

/** Kind of a chrome node's `BoxChange` (no id): tweened with its element's pass. */
export const CHROME_KIND = 'chrome';

export function applyLayout(ctx: LayoutDomContext, result: LayoutResult): BoxChange[] {
  const changes: BoxChange[] = [];
  chromeChanges = changes;
  // The scroll views' state syncs read scroll metrics; batched, they share
  // one layout after the pass's writes instead of forcing one each.
  beginScrollSyncBatch();
  try {
    applyFrames(ctx, result, changes);
  } finally {
    chromeChanges = null;
    flushScrollSyncBatch();
  }
  return changes;
}

function applyFrames(ctx: LayoutDomContext, result: LayoutResult, changes: BoxChange[]): void {
  // Phase 9: an incremental result names what it touched; everything else
  // kept its frame, chrome and box. A one-off result is visited whole.
  const boxes = result.changed ? ctx.boxes : new Map<number, Rect>();
  let ids: Iterable<number>;
  if (result.changed) {
    const visit = new Set(result.changed);
    for (const id of ctx.dirty) visit.add(id);
    ids = visit;
  } else {
    ids = result.frames.keys();
  }

  /** The element's box relative to the origin its `left`/`top` use; applied when it differs from the last pass's. */
  const applyBox = (id: number, node: LayoutNode, el: HTMLElement, frame: Rect): { box: Rect; moved: boolean; previous: Rect | null } => {
    const box = drawnBox(node, boxOf(ctx, result, node, frame));
    const origin =
      node.kind === 'sheet'
        ? { x: 0, y: 0 }
        : isSectionSupplement(node)
          ? supplementOrigin(ctx, result, id)
          : isLargeTitleToolbar(node)
            ? largeTitleToolbarOrigin(ctx, result, id)
            : originOf(ctx, result, ctx.parents.get(id) ?? ROOT_ID);
    const relative = { x: box.x - origin.x, y: box.y - origin.y, width: box.width, height: box.height };
    const previous = ctx.boxes.get(id) ?? null;
    boxes.set(id, relative);
    const moved = !previous || !sameRect(previous, relative);
    if (moved) changes.push({ id, el, kind: node.kind, from: previous, to: relative });
    if (moved || ctx.dirty.has(id)) {
      if (node.kind === 'sheet') positionSheetCard(el, box);
      else positionBox(el, box, origin);
    }
    return { box, moved, previous };
  };

  const visited = result.changed ? new Set(ids) : null;
  for (const id of ids) {
    const frame = result.frames.get(id);
    const el = ctx.elements.get(id);
    const node = ctx.nodes.get(id);
    if (!el || !node) continue;
    if (!frame) {
      boxes.delete(id);
      continue;
    }
    const { box, moved, previous } = applyBox(id, node, el, frame);
    // A former menu item that is laid out again (moved out of the menu), or a
    // `viewthatfits` candidate chosen again, shows again.
    if (el.hidden) el.hidden = false;
    applyChrome(result, node, el, box, frame, scrollBottomInset(ctx, result, node, frame), previous === null, changes);
    if (node.kind === 'tab') setTabHidden(el, result.hidden.has(id));
    // A `sidebar` list draws as one (no cards or hairlines) in a regular width; in a compact one it looks like `insetGrouped`.
    if (node.kind === 'list') setFlag(el, 'sbListLook', (node.props['style'] === 'sidebar' || result.chrome.get(id)?.['sidebarLook']) && isRegularWidth(ctx.env.screen) ? 'sidebar' : null);
    // A child the engine did not touch kept its screen frame, but if this
    // element's box moved, the child's `left`/`top` (relative to it) did
    // not: the plus button at the bottom of a chat's composer stays put on
    // screen while the composer grows upwards around it. Re-place such
    // children; their own subtrees are relative to them and unchanged.
    if (!moved || !visited) continue;
    for (const child of node.children) {
      if (visited.has(child.id)) continue;
      const childFrame = result.frames.get(child.id);
      const childEl = ctx.elements.get(child.id);
      if (childFrame && childEl) applyBox(child.id, child, childEl, childFrame);
    }
  }

  // Phase 6: menu items are hidden without a frame (tabs keep theirs, above);
  // Phase 7: so are the unchosen candidates of a `viewthatfits`.
  for (const id of result.hidden) {
    const el = ctx.elements.get(id);
    const node = ctx.nodes.get(id);
    if (!el || !node || node.kind === 'tab' || result.frames.has(id)) continue;
    if (!el.hidden) el.hidden = true;
  }

  ctx.boxes = boxes;
  ctx.dirty.clear();
}

const LIST_ROW_MIN_HEIGHT = 44;
const LIST_ROW_VPAD = 11;

/** Hide a non-selected tab without dropping its DOM (or its scroll positions). */
export function setTabHidden(tab: HTMLElement, hidden: boolean): void {
  tab.toggleAttribute('data-sb-tab-hidden', hidden);
  tab.toggleAttribute('inert', hidden);
  tab.setAttribute('aria-hidden', String(hidden));
}

/**
 * The DOM box of an element: its full row inside a list, else its frame.
 * Rows whose own chrome replaced the engine's `row` key (toggles, bordered
 * buttons) get the row rebuilt from the card and the engine's row rule
 * (content centered in max(44, content + 2 × 11)).
 */
export function boxOf(ctx: LayoutDomContext, result: LayoutResult, node: LayoutNode, frame: Rect): Rect {
  const row = result.chrome.get(node.id)?.['row'];
  if (row) return row;
  if (node.kind === 'section' || isSectionSupplement(node)) return frame;
  const parentId = ctx.parents.get(node.id);
  const parent = parentId !== undefined ? ctx.nodes.get(parentId) : undefined;
  if (!parent || (parent.kind !== 'section' && parent.kind !== 'list')) return frame;
  const card = parent.kind === 'section' ? result.chrome.get(parent.id)?.['rows'] : result.frames.get(parent.id);
  if (!card) return frame;
  const height = Math.max(LIST_ROW_MIN_HEIGHT, frame.height + 2 * LIST_ROW_VPAD);
  return { x: card.x, y: frame.y - (height - frame.height) / 2, width: card.width, height };
}

/**
 * The box an element is drawn in: its frame, except a circle's, which is the
 * square centered in it (SwiftUI draws the largest circle that fits, so
 * `Circle().frame(width: 110)` in a taller space stays round). The engine's
 * frame, which the siblings were laid out with, is unchanged.
 */
function drawnBox(node: LayoutNode, box: Rect): Rect {
  if (node.kind !== 'shape' || node.props['shape'] !== 'circle' || box.width === box.height) return box;
  const side = Math.min(box.width, box.height);
  return { x: box.x + (box.width - side) / 2, y: box.y + (box.height - side) / 2, width: side, height: side };
}

/** A section's view header or footer: not a row, and a DOM child of the section itself. */
function isSectionSupplement(node: LayoutNode): boolean {
  return node.kind === 'sectionheader' || node.kind === 'sectionfooter';
}

/** A view header / footer sits in the section element, beside the rows' card: the section's box is its origin. */
function supplementOrigin(ctx: LayoutDomContext, result: LayoutResult, id: number): { x: number; y: number } {
  const parentId = ctx.parents.get(id);
  const parent = parentId === undefined ? undefined : ctx.nodes.get(parentId);
  const frame = parentId === undefined ? undefined : result.frames.get(parentId);
  if (!parent || !frame || parent.kind !== 'section') return originOf(ctx, result, parentId ?? ROOT_ID);
  return boxOf(ctx, result, parent, frame);
}

/** A `.largeTitle` toolbar: the renderer keeps it in its screen's large title element. */
export function isLargeTitleToolbar(node: LayoutNode): boolean {
  return node.kind === 'toolbar' && node.props['placement'] === 'largeTitle';
}

/** A `.largeTitle` toolbar sits in the large title element, which is placed at the screen's `largeTitle` row. */
function largeTitleToolbarOrigin(ctx: LayoutDomContext, result: LayoutResult, id: number): { x: number; y: number } {
  const parentId = ctx.parents.get(id) ?? ROOT_ID;
  const row = result.chrome.get(parentId)?.['largeTitle'];
  return row ?? originOf(ctx, result, parentId);
}

/** Screen-coordinate origin that the children of `parentId` are positioned against. */
export function originOf(ctx: LayoutDomContext, result: LayoutResult, parentId: number): { x: number; y: number } {
  if (parentId === ROOT_ID) return { x: 0, y: 0 };
  const node = ctx.nodes.get(parentId);
  const frame = result.frames.get(parentId);
  if (!node || !frame) return { x: 0, y: 0 };
  const chrome = result.chrome.get(parentId);
  switch (node.kind) {
    case 'scrollview':
      return chrome?.['content'] ?? frame;
    case 'list':
    case 'sheet':
      return frame;
    case 'section':
      return chrome?.['rows'] ?? frame;
    default:
      return boxOf(ctx, result, node, frame);
  }
}

/**
 * iOS adds the bottom safe area as a content inset to scrolling views that
 * reach the bottom of the screen, so the last row can scroll clear of the
 * home indicator. Inside a sheet the card already ends above it (0).
 */
function scrollBottomInset(ctx: LayoutDomContext, result: LayoutResult, node: LayoutNode, frame: Rect): number {
  if (node.kind !== 'list' && node.kind !== 'scrollview') return 0;
  // Under a tab bar the engine reports the covered part of the frame; the
  // bar (49 + safe area) replaces the plain safe-area inset.
  const covered = result.chrome.get(node.id)?.['bottomInset'];
  if (covered) return covered.height;
  if (frame.y + frame.height < ctx.env.screen.height) return 0;
  for (let id = ctx.parents.get(node.id); id !== undefined && id !== ROOT_ID; id = ctx.parents.get(id)) {
    if (ctx.nodes.get(id)?.kind === 'sheet') return 0;
  }
  return ctx.env.safeArea.bottom;
}

function sameRect(a: Rect, b: Rect): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

function positionBox(el: HTMLElement, box: Rect, origin: { x: number; y: number }): void {
  const s = el.style;
  s.left = `${box.x - origin.x}px`;
  s.top = `${box.y - origin.y}px`;
  s.width = `${box.width}px`;
  s.height = `${box.height}px`;
}

/** Position a chrome node inside its element: `rect` is in screen coordinates. */
/**
 * An edit-mode control fades in when it first appears (the list started
 * editing), marked `data-sb-appear` for the animation's duration. A control
 * that is laid out again while shown, or whose row is moved in the DOM (a
 * reorder re-inserts the row, which would restart a CSS animation on it),
 * is not marked and stays put.
 */
function placeEditControl(node: HTMLElement | null, rect: Rect | undefined, origin: Rect): void {
  if (!node) return;
  const wasHidden = node.hidden;
  placeChrome(node, rect, origin);
  if (wasHidden && !node.hidden) {
    node.dataset['sbAppear'] = '';
    const settle = (): void => {
      delete node.dataset['sbAppear'];
    };
    node.addEventListener('animationend', settle, { once: true });
    node.addEventListener('animationcancel', settle, { once: true });
  }
}

/**
 * iOS 26: the glass a screen's bar items float on, one element per capsule
 * (`.sb-bar-glass`, styles.css): the navigation bar's runs of leading and
 * trailing items (engine `leadingGlass0` …, `trailingGlass0` …) and the
 * bottom bar's (`bottomGlass0` …), under the items. A capsule is known by
 * the toolbars drawn on it (`data-sb-toolbars`: the ids of those of its
 * placement whose frames lie in it), not by its index, so a group that goes
 * takes its own capsule with it and the groups after it keep theirs, and an
 * item swapped for another (its toolbar's element kept, Toolbar.swift)
 * changes on a capsule that stays. A capsule that moves or resizes is
 * reported in `changes` (kind `barglass`, no id) so the Animator tweens it
 * with the toolbar's items; one that appears on a screen already there, or
 * goes, scales and fades as the items do (`TOOLBAR_TRANSITION`), in the
 * toolbar's own spring.
 */
const BAR_PLACEMENTS = new Set(['leading', 'trailing', 'bottom']);

function syncBarGlass(
  result: LayoutResult,
  node: LayoutNode,
  screen: HTMLElement,
  chrome: Record<string, Rect> | undefined,
  frame: Rect,
  screenIsNew: boolean,
  changes: BoxChange[],
): void {
  for (const placement of ['leading', 'trailing', 'bottom'] as const) {
    const unmatched = new Set(screen.querySelectorAll<HTMLElement>(`:scope > .sb-bar-glass[data-sb-placement="${placement}"]:not([data-sb-exiting])`));
    // A capsule's toolbars are those whose frames lie in it, of any bar
    // placement: on a Mac a bottom toolbar's items are on the trailing capsules.
    const toolbars = node.children.filter((child) => child.kind === 'toolbar' && BAR_PLACEMENTS.has(String(child.props['placement'])));
    for (let count = 0, rect = chrome?.[`${placement}Glass0`]; rect; rect = chrome?.[`${placement}Glass${++count}`]) {
      const capsule = rect;
      const ids = toolbars.filter((toolbar) => centeredIn(result.frames.get(toolbar.id), capsule)).map((toolbar) => String(toolbar.id));
      const relative = { x: rect.x - frame.x, y: rect.y - frame.y, width: rect.width, height: rect.height };
      let glass = Array.from(unmatched).find((candidate) => (candidate.dataset['sbToolbars'] ?? '').split(' ').some((id) => ids.includes(id)));
      if (glass) {
        unmatched.delete(glass);
        const from = glassBox(glass);
        if (!sameRect(from, relative)) changes.push({ id: -1, el: glass, kind: BAR_GLASS_KIND, from, to: relative });
      } else {
        glass = document.createElement('div');
        glass.className = 'sb-bar-glass';
        glass.dataset['sbPlacement'] = placement;
        screen.append(glass);
        if (!screenIsNew) transitionGlass(glass, false);
        changes.push({ id: -1, el: glass, kind: BAR_GLASS_KIND, from: null, to: relative });
      }
      glass.dataset['sbToolbars'] = ids.join(' ');
      placeChrome(glass, rect, frame, false);
    }
    for (const extra of unmatched) transitionGlass(extra, true);
  }
}

/** Whether `rect`'s center lies in `area` (a toolbar's frame spans its items, all on one capsule). */
function centeredIn(rect: Rect | undefined, area: Rect): boolean {
  if (!rect) return false;
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  return cx >= area.x && cx <= area.x + area.width && cy >= area.y && cy <= area.y + area.height;
}

/** The box a glass capsule was placed at (`placeChrome`), relative to its screen. */
function glassBox(glass: HTMLElement): Rect {
  const s = glass.style;
  return { x: parseFloat(s.left) || 0, y: parseFloat(s.top) || 0, width: parseFloat(s.width) || 0, height: parseFloat(s.height) || 0 };
}

/** Scales and fades a bar capsule in, or out (then removes it), as its toolbar's items do. */
function transitionGlass(glass: HTMLElement, exit: boolean): void {
  const timing = baseTiming(timingFor(TOOLBAR_ANIMATION));
  const gone = transitionStart(TOOLBAR_TRANSITION, exit);
  const frames: Keyframe[] = [
    { opacity: gone.opacity ?? 0, transform: gone.transform ?? 'none' },
    { opacity: 1, transform: 'none' },
  ];
  if (!exit) {
    glass.animate(frames, { ...timing, fill: 'backwards' });
    return;
  }
  glass.toggleAttribute('data-sb-exiting', true);
  const out = glass.animate(frames.slice().reverse(), { ...timing, fill: 'forwards' });
  const done = (): void => glass.remove();
  out.finished.then(done, done);
  window.setTimeout(done, timing.duration + timing.delay + 100);
}

/**
 * Position a chrome node inside its element: `rect` is in screen coordinates.
 * A node that was shown and moves or resizes is reported (kind `chrome`), so
 * an animated pass tweens it with the boxes around it: a section's card, a
 * row's badge or chevron follow their row as a split view's column widens,
 * instead of jumping to the end (`report` false: the caller reports it).
 */
function placeChrome(node: HTMLElement | null, rect: Rect | undefined, origin: Rect, report = true): void {
  if (!node) return;
  if (!rect) {
    node.hidden = true;
    return;
  }
  const s = node.style;
  const to = { x: rect.x - origin.x, y: rect.y - origin.y, width: rect.width, height: rect.height };
  if (report && chromeChanges && !node.hidden && s.position === 'absolute') {
    const from = { x: parseFloat(s.left) || 0, y: parseFloat(s.top) || 0, width: parseFloat(s.width) || 0, height: parseFloat(s.height) || 0 };
    if (!sameRect(from, to)) chromeChanges.push({ id: -1, el: node, kind: CHROME_KIND, from, to });
  }
  node.hidden = false;
  s.position = 'absolute';
  s.left = `${to.x}px`;
  s.top = `${to.y}px`;
  s.width = `${to.width}px`;
  s.height = `${to.height}px`;
}

function positionSheetCard(sheet: HTMLElement, frame: Rect): void {
  const card = sheet.querySelector<HTMLElement>(':scope > .sb-sheet-card');
  if (!card) return;
  const s = card.style;
  s.left = `${frame.x}px`;
  s.top = `${frame.y}px`;
  s.width = `${frame.width}px`;
  s.height = `${frame.height}px`;
}

/**
 * `lines` > 1: a paragraph, whose lines are a leading apart (the engine makes
 * it the first line's height plus a leading per further line).
 */
function setFont(el: HTMLElement, font: ResolvedFont | undefined, lines = 1): void {
  if (!font) return;
  const css = lines > 1 ? font.css.replace(/\/[\d.]+px /, `/${Math.round(font.leading * 100) / 100}px `) : font.css;
  if (el.style.font !== css) el.style.font = css;
  // The `font` shorthand resets the variant, so it goes after it.
  const variant = font.monospacedDigit ? 'tabular-nums' : '';
  if (el.style.fontVariantNumeric !== variant) el.style.fontVariantNumeric = variant;
}

const TEXT_ALIGN: Record<string, string> = { leading: 'left', center: 'center', trailing: 'right' };

/** Render the engine's exact lines into a text node (`white-space: pre` keeps them). */
function setTextLayout(el: HTMLElement, layout: TextLayout): void {
  setFont(el, layout.font, layout.lines.length);
  const align = TEXT_ALIGN[layout.alignment] ?? 'left';
  if (el.style.textAlign !== align) el.style.textAlign = align;
  if (layout.rich && el.dataset['sbRich'] !== undefined) {
    drawRichText(el, layout.rich);
    return;
  }
  const content = layout.lines.join('\n');
  if (el.textContent !== content) el.textContent = content;
}

/**
 * Phase 6 `.badge`: the trailing text of a list row, drawn by the row element
 * (a `styled` row, or a `navlink` whose label carries the badge) at
 * `chrome.badge`; removed when the row has no badge.
 */
function applyBadge(result: LayoutResult, node: LayoutNode, el: HTMLElement, box: Rect): void {
  const badgeRect = result.chrome.get(node.id)?.['badge'];
  // A swipeable row draws its content on a layer that slides over its
  // actions; the badge is part of that content.
  const host = el.querySelector<HTMLElement>(':scope > .sb-swipe-content') ?? el;
  let badge = host.querySelector<HTMLElement>(':scope > .sb-badge');
  if (!badgeRect) {
    badge?.remove();
    return;
  }
  if (!badge) {
    badge = el.ownerDocument.createElement('span');
    badge.className = 'sb-badge';
    badge.setAttribute('aria-hidden', 'true');
    host.append(badge);
  }
  placeChrome(badge, badgeRect, box);
  const layout = result.chromeText.get(node.id)?.['badge'];
  if (layout) {
    setFont(badge, layout.font);
    const text = layout.lines[0] ?? '';
    if (badge.textContent !== text) badge.textContent = text;
  }
}

function setFlag(el: HTMLElement, key: string, value: string | null): void {
  if (value === null) {
    if (key in el.dataset) delete el.dataset[key];
  } else if (el.dataset[key] !== value) {
    el.dataset[key] = value;
  }
}

/**
 * A host's content element goes back into the engine's frame when the host
 * is drawn in a bigger box (a list row's): src/hostInset.ts. In its own
 * frame the stylesheet's `width: 100%` (and `height: 100%` for `fill`) hold.
 */
function positionHostContent(el: HTMLElement, box: Rect, frame: Rect): void {
  const content = el.querySelector<HTMLElement>(':scope > .sb-host-content');
  if (!content) return;
  const inset = hostContentInset(box, frame);
  const s = content.style;
  const left = inset ? `${inset.left}px` : '';
  const top = inset ? `${inset.top}px` : '';
  const width = inset ? `${inset.width}px` : '';
  const height = inset ? `${inset.height}px` : '';
  if (s.left !== left) s.left = left;
  if (s.top !== top) s.top = top;
  if (s.width !== width) s.width = width;
  if (s.height !== height) s.height = height;
}

/**
 * For leaf rows: pad the content frame back into place inside the row box.
 * `shift` moves vertically centered content (a one-line field's text) down
 * (or up, negative) by padding twice as much above it (or below).
 */
function padIntoFrame(el: HTMLElement, box: Rect, frame: Rect, shift = 0): void {
  const s = el.style;
  const left = frame.x - box.x;
  const top = frame.y - box.y;
  const right = box.x + box.width - (frame.x + frame.width);
  const bottom = box.y + box.height - (frame.y + frame.height);
  const above = Math.max(0, top) + Math.max(0, 2 * shift);
  const below = Math.max(0, bottom) + Math.max(0, -2 * shift);
  if (left || top || right || bottom) {
    s.padding = `${above}px ${Math.max(0, right)}px ${below}px ${Math.max(0, left)}px`;
    return;
  }
  // Only the shift: above and below alone, the sides left to the stylesheet
  // (a rounded border field's inset).
  if (s.paddingLeft || s.paddingRight) s.padding = '';
  const paddingTop = above ? `${above}px` : '';
  const paddingBottom = below ? `${below}px` : '';
  if (s.paddingTop !== paddingTop) s.paddingTop = paddingTop;
  if (s.paddingBottom !== paddingBottom) s.paddingBottom = paddingBottom;
}

/**
 * A vertical field's textarea draws every line a leading apart, where the
 * engine sized it as a Text: its first line a line height tall, the rest a
 * leading apart. The textarea's lines need the difference more (2 px for
 * the body style); without it they overflow by that much, and the textarea
 * scrolls its text up while it is edited. The difference comes out of the
 * padding below the lines (a list row's, a rounded border's), else the box
 * reaches that much further down (into the space below it, which its lines
 * leave empty).
 */
function fitFieldLines(el: HTMLTextAreaElement, font: ResolvedFont, box: Rect, frame: Rect): void {
  const extra = Math.ceil(font.leading - font.lineHeight - 0.01);
  if (extra <= 0) return;
  const s = el.style;
  const below = box.y + box.height - (frame.y + frame.height);
  const padding = below > 0 ? below : el.dataset['sbStyle'] === 'roundedBorder' ? ROUNDED_FIELD_INSET : 0;
  s.paddingBottom = `${Math.max(0, padding - extra)}px`;
  const height = `${box.height + Math.max(0, extra - padding)}px`;
  if (s.height !== height) s.height = height;
}

/** A rounded border field's padding above and below its lines (styles.css: the engine's inset of 6, less the 1 px border). */
const ROUNDED_FIELD_INSET = 5;

const fieldCapShifts = new Map<string, number>();

/**
 * How far a one-line field's text must move so its capitals sit where a
 * text's do: styles.css trims a text's first line to its cap height and puts
 * the capitals `--sb-cap-inset` (SF Pro's 0.2476em) below the line's top,
 * whatever font the page has, but an input lays its line out with the
 * font's own metrics. Where those put the capitals is measured in the page
 * (canvas may resolve a font list to another face): a line trimmed to its
 * cap height is shorter than an untrimmed one by just that. Once per font;
 * none where texts are not trimmed (both then use the font's metrics).
 */
function fieldCapShift(font: ResolvedFont): number {
  const cached = fieldCapShifts.get(font.css);
  if (cached !== undefined) return cached;
  let shift = 0;
  if (typeof CSS !== 'undefined' && CSS.supports('text-box', 'trim-start cap alphabetic') && document.body) {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:pre';
    const line = (trimmed: boolean): HTMLElement => {
      const el = document.createElement('div');
      el.style.font = font.css;
      if (trimmed) el.style.setProperty('text-box', 'trim-start cap alphabetic');
      el.textContent = 'H';
      return probe.appendChild(el);
    };
    const whole = line(false);
    const trimmed = line(true);
    document.body.appendChild(probe);
    const natural = whole.getBoundingClientRect().height - trimmed.getBoundingClientRect().height;
    probe.remove();
    shift = Math.round((0.2476 * font.size - natural) * 100) / 100;
  }
  // a web font still loading measures as its fallback: measure again next time
  if (typeof document === 'undefined' || !document.fonts || document.fonts.status === 'loaded') fieldCapShifts.set(font.css, shift);
  return shift;
}

function applyChrome(
  result: LayoutResult,
  node: LayoutNode,
  el: HTMLElement,
  box: Rect,
  frame: Rect,
  bottomInset: number,
  isNew: boolean,
  changes: BoxChange[],
): void {
  const chrome = result.chrome.get(node.id);
  // `.listRowSeparator(.hidden)`: a list row leaves out the separator below
  // it, or above it (the previous row's line; styles.css).
  setFlag(el, 'sbSeparatorBottom', chrome?.['separatorBottomHidden'] ? 'hidden' : null);
  setFlag(el, 'sbSeparatorTop', chrome?.['separatorTopHidden'] ? 'hidden' : null);
  // `.listRowBackground(_:)`: the row's cell paint (styles.css reads it, and
  // a card with such a row leaves its own background to its rows; a card of
  // clear rows alone, marked `clear`, clips nothing either).
  const rowBackground = chrome?.['row'] ? listRowBackgroundOf(node) : undefined;
  const rowPaint = rowBackground !== undefined ? styleToCSS(rowBackground as ShapeStyle, 'paint').value : '';
  if (el.style.getPropertyValue('--sb-row-background') !== rowPaint) {
    if (rowPaint) el.style.setProperty('--sb-row-background', rowPaint);
    else el.style.removeProperty('--sb-row-background');
  }
  setFlag(el, 'sbRowBackground', rowPaint ? (rowPaint === 'transparent' ? 'clear' : '') : null);
  // iOS 26: the separator under a list row starts at its first text (styles.css).
  const separator = chrome?.['separator'];
  const leading = separator ? `${separator.x}px` : '';
  if (el.style.getPropertyValue('--sb-separator-leading') !== leading) {
    if (leading) el.style.setProperty('--sb-separator-leading', leading);
    else el.style.removeProperty('--sb-separator-leading');
  }
  switch (node.kind) {
    case 'styled':
      applyBadge(result, node, el, box);
      return;
    case 'text': {
      const layout = result.text.get(node.id);
      if (layout) setTextLayout(el, layout);
      padIntoFrame(el, box, frame);
      return;
    }
    case 'host':
      positionHostContent(el, box, frame);
      return;
    case 'image': {
      const font = result.fonts.get(node.id);
      if (font) {
        const size = `${font.size}px`;
        if (el.style.fontSize !== size) el.style.fontSize = size;
      }
      padIntoFrame(el, box, frame);
      // A bitmap is drawn for the size it is shown at.
      if (node.props['bitmap']) bitmapLaidOut(el, frame.width, frame.height);
      return;
    }
    case 'textfield':
    case 'texteditor': {
      // A field draws in its context's font (a vertical one, or a rich
      // editor, the font its lines were measured in, a leading apart).
      const font = result.fonts.get(node.id);
      setFont(el, font, el instanceof HTMLInputElement ? 1 : 2);
      // A one-line field's text sits where a centered Text's would (the
      // engine's `text` rect, snapped as a label beside it is): the input
      // centers its line in the frame, so move it by the difference.
      const line = chrome?.['text'];
      const shift =
        el instanceof HTMLInputElement && font
          ? fieldCapShift(font) + (line ? line.y - frame.y - (frame.height - font.lineHeight) / 2 : 0)
          : 0;
      padIntoFrame(el, box, frame, shift);
      if (el instanceof HTMLTextAreaElement && node.kind === 'textfield' && font) fitFieldLines(el, font, box, frame);
      // A rich editor's runs, each in the font it was measured in.
      if (isRichEditor(el)) layOutRichEditor(el, result.text.get(node.id));
      return;
    }
    // Phase 8 controls: their chrome sits at the engine's rects inside the element's box.
    case 'stepper':
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-stepper-control'), chrome?.['control'], box);
      // The Mac idiom: the arrows, up over down (styles.css).
      setFlag(el, 'sbArrows', chrome?.['arrows'] ? '' : null);
      return;
    case 'slider': {
      // The `label` child is hidden (iOS draws none) and names the slider, as VoiceOver reads it.
      const names = Array.isArray(node.props['labels']) ? node.props['labels'] : [];
      const labelNode = node.children[names.indexOf('label')];
      const labelEl = labelNode && names.includes('label') ? el.querySelector<HTMLElement>(`[data-sb-id="${labelNode.id}"]`) : null;
      if (labelEl) {
        if (!labelEl.id) labelEl.id = `sb-slider-label-${labelNode!.id}`;
        el.setAttribute('aria-labelledby', labelEl.id);
      } else {
        el.removeAttribute('aria-labelledby');
      }
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-slider-track'), chrome?.['track'], box);
      // The Mac idiom: the round knob (styles.css).
      setFlag(el, 'sbKnob', chrome?.['knob'] ? '' : null);
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-slider-fill'), chrome?.['fill'], box);
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-slider-thumb'), chrome?.['thumb'], box);
      return;
    }
    case 'datepicker':
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-datepicker-date'), chrome?.['date'], box);
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-datepicker-time'), chrome?.['time'], box);
      // The Mac idiom: the date and time as bordered fields (styles.css).
      setFlag(el, 'sbFields', chrome?.['fields'] ? '' : null);
      return;
    case 'alert': {
      // Phase 8: the card(s), the centered title / message lines and the
      // renderer-drawn OK; the button children have ordinary frames. Two
      // buttons in one row get `data-sb-layout="row"` (a hairline between
      // them); the appear animation scales the layer about the card's center.
      const layer = el.querySelector<HTMLElement>(':scope > .sb-alert-layer');
      const part = (cls: string): HTMLElement | null => layer?.querySelector<HTMLElement>(`:scope > .${cls}`) ?? null;
      const texts = result.chromeText.get(node.id);
      const card = chrome?.['card'];
      placeChrome(part('sb-alert-card'), card, box);
      placeChrome(part('sb-alert-cancel-card'), chrome?.['cancelCard'], box);
      const title = part('sb-alert-title');
      placeChrome(title, chrome?.['title'], box);
      if (title && texts?.['title']) setTextLayout(title, texts['title']);
      const message = part('sb-alert-message');
      placeChrome(message, chrome?.['message'], box);
      if (message && texts?.['message']) setTextLayout(message, texts['message']);
      placeChrome(part('sb-alert-ok'), chrome?.['ok'], box);
      el.toggleAttribute('data-sb-header', Boolean(chrome?.['title'] || chrome?.['message']));
      // The Mac idiom: the alert as a panel (styles.css).
      setFlag(el, 'sbPanel', chrome?.['panel'] ? '' : null);
      const first = chrome?.['button0'];
      const second = chrome?.['button1'];
      el.dataset['sbLayout'] = first && second && first.y === second.y && !chrome?.['button2'] ? 'row' : 'stack';
      if (layer && card) {
        layer.style.transformOrigin = `${card.x + card.width / 2 - box.x}px ${card.y + card.height / 2 - box.y}px`;
      }
      return;
    }
    case 'menu':
      // The Mac idiom: a pull-down button around the label (styles.css).
      setFlag(el, 'sbPulldown', chrome?.['pulldown'] ? '' : null);
      // A Mac toolbar menu: its indicator's chevron after the label (styles.css).
      setFlag(el, 'sbIndicator', chrome?.['indicator'] ? '' : null);
      return;
    case 'button': {
      // The Mac idiom: an automatic button the engine bordered (styles.css);
      // in a list row the button is drawn at its own frame on the row
      // (`inset`, the frame in the row's coordinates).
      // The rect the border is drawn at: the engine's (a bar item's slot) or
      // the button's own frame; `inset` when it is not the element's box.
      const bordered = chrome?.['bordered'];
      const rect = bordered ?? frame;
      const inset = bordered !== undefined && (box.x !== rect.x || box.y !== rect.y || box.width !== rect.width || box.height !== rect.height);
      setFlag(el, 'sbBordered', bordered ? (inset ? 'inset' : '') : null);
      const vars: [string, string][] = inset
        ? [['--sb-bordered-left', `${rect.x - box.x}px`], ['--sb-bordered-top', `${rect.y - box.y}px`], ['--sb-bordered-width', `${rect.width}px`], ['--sb-bordered-height', `${rect.height}px`]]
        : [['--sb-bordered-left', ''], ['--sb-bordered-top', ''], ['--sb-bordered-width', ''], ['--sb-bordered-height', '']];
      for (const [name, value] of vars) {
        if (el.style.getPropertyValue(name) !== value) {
          if (value) el.style.setProperty(name, value);
          else el.style.removeProperty(name);
        }
      }
      return;
    }
    case 'toggle': {
      // The Mac idiom: a checkbox at the switch's rect (styles.css).
      setFlag(el, 'sbCheckbox', chrome?.['checkbox'] ? '' : null);
      const sw = el.querySelector<HTMLElement>(':scope > .sb-switch');
      const rect = chrome?.['switch'] ?? {
        x: frame.x + frame.width - SWITCH_SIZE.width,
        y: frame.y + (frame.height - SWITCH_SIZE.height) / 2,
        ...SWITCH_SIZE,
      };
      placeChrome(sw, rect, box);
      return;
    }
    case 'disclosuregroup': {
      const chevron = el.querySelector<HTMLElement>(':scope > .sb-disclosure-chevron');
      const toggle = el.querySelector<HTMLElement>(':scope > .sb-disclosure-toggle');
      // In a list the group is an outline (engine.ts `outlineRows`): its label
      // and content are rows (styles.css draws them as the card's), the toggle
      // lies over the label's row and the chevron sits at that row's trailing
      // edge, centred as a link's is.
      const outline = chrome?.['toggle'];
      setFlag(el, 'sbOutline', outline ? '' : null);
      if (outline) {
        placeChrome(toggle, outline, box);
        const rect = chrome?.['chevron'];
        placeChrome(chevron, rect ? { x: rect.x + (rect.width - CHEVRON_SIZE) / 2, y: rect.y + (rect.height - CHEVRON_SIZE) / 2, width: CHEVRON_SIZE, height: CHEVRON_SIZE } : undefined, box);
        return;
      }
      // The chevron at the label's trailing edge, the toggle over the label (the first child's frame).
      const label = node.children[0] ? result.frames.get(node.children[0].id) : undefined;
      if (label) {
        placeChrome(chevron, { x: frame.x + frame.width - CHEVRON_SIZE - 2, y: label.y + (label.height - CHEVRON_SIZE) / 2, width: CHEVRON_SIZE, height: CHEVRON_SIZE }, box);
        placeChrome(toggle, { x: frame.x, y: label.y, width: frame.width, height: label.height }, box);
      } else {
        placeChrome(chevron, undefined, box);
        placeChrome(toggle, undefined, box);
      }
      return;
    }
    case 'navlink': {
      // A link row's badge sits on its label (`NavigationLink { row.badge(n) }`).
      applyBadge(result, node, el, box);
      const chevron = el.querySelector<HTMLElement>(':scope > .sb-navlink-chevron');
      const rect = chrome?.['chevron'];
      if (chevron) {
        if (rect) {
          chevron.removeAttribute('hidden');
          placeChrome(chevron, {
            x: rect.x + (rect.width - CHEVRON_SIZE) / 2,
            y: rect.y + (rect.height - CHEVRON_SIZE) / 2,
            width: CHEVRON_SIZE,
            height: CHEVRON_SIZE,
          }, box);
        } else {
          chevron.toggleAttribute('hidden', true); // an SVG: the `hidden` property is not an attribute
        }
      }
      return;
    }
    case 'section': {
      const rows = el.querySelector<HTMLElement>(':scope > .sb-section-rows');
      const header = el.querySelector<HTMLElement>(':scope > .sb-section-header');
      const footer = el.querySelector<HTMLElement>(':scope > .sb-section-footer');
      const texts = result.chromeText.get(node.id);
      if (chrome?.['rows']) placeChrome(rows, chrome['rows'], frame);
      else if (rows) {
        // Outside a list the section is a plain container.
        rows.hidden = false;
        rows.removeAttribute('style');
      }
      placeChrome(header, chrome?.['header'], frame);
      placeChrome(footer, chrome?.['footer'], frame);
      if (header && texts?.['header']) setTextLayout(header, texts['header']);
      if (footer && texts?.['footer']) {
        // The prop text in the engine's font and rect, wrapped there.
        setFont(footer, texts['footer'].font, texts['footer'].lines.length);
        footer.style.textAlign = 'left';
        footer.style.whiteSpace = 'pre-wrap';
        const original = typeof node.props['footer'] === 'string' ? node.props['footer'] : texts['footer'].lines.join('\n');
        if (footer.textContent !== original) footer.textContent = original;
      }
      return;
    }
    case 'list': {
      const content = el.querySelector<HTMLElement>(':scope > .sb-list-content');
      const size = result.contentSizes.get(node.id);
      let extent: ScrollExtent | undefined;
      if (content && size) {
        content.style.width = `${size.width}px`;
        // Extra scrollable space at the end (the engine's frames are unchanged).
        content.style.height = `${size.height + bottomInset}px`;
        extent = { content: size.height + bottomInset, viewport: frame.height };
      }
      bindScrollUnderBar(el, frame, chrome?.['topInset'], chrome?.['largeTitle']);
      bindScrollEdgeBottom(el, chrome?.['bottomInset'], extent);
      // The Mac idiom's columns form (`data-sb-form-columns`, styles.css):
      // the label column's texts (chrome `formLabel<i>`) as spans in the
      // content, as many as the engine placed.
      setFlag(el, 'sbFormColumns', chrome?.['columns'] ? '' : null);
      setFlag(el, 'sbFormGrouped', chrome?.['grouped'] ? '' : null);
      if (content) {
        const texts = result.chromeText.get(node.id);
        const labels = content.querySelectorAll<HTMLElement>(':scope > .sb-form-label');
        let i = 0;
        for (; chrome?.[`formLabel${i}`]; i++) {
          let label = labels[i] ?? null;
          if (!label) {
            label = document.createElement('span');
            label.className = 'sb-form-label';
            content.append(label);
          }
          placeChrome(label, chrome[`formLabel${i}`], frame);
          const layout = texts?.[`formLabel${i}`];
          if (layout) setTextLayout(label, layout);
        }
        for (let extra = i; extra < labels.length; extra++) labels[extra]!.remove();
      }
      return;
    }
    case 'scrollview': {
      const content = el.querySelector<HTMLElement>(':scope > .sb-scroll-content');
      const size = result.contentSizes.get(node.id);
      const rect = chrome?.['content'];
      let extent: ScrollExtent | undefined;
      if (content && size) {
        const s = content.style;
        const axes = String(node.props['axes'] ?? 'vertical');
        // `.scrollClipDisabled()`: the frame reaches past the viewport the
        // scroll view laid out in (chrome `viewport`), by `before` and
        // `after`. The content keeps its place (its margin is `before`) and
        // runs `after` further, so it scrolls as far as it did and snaps
        // against the viewport's edges.
        const viewport = chrome?.['viewport'];
        const before = viewport ? viewport.x - frame.x : 0;
        const after = viewport ? frame.x + frame.width - (viewport.x + viewport.width) : 0;
        s.width = `${size.width + after}px`;
        s.height = `${size.height + (axes === 'horizontal' ? 0 : bottomInset)}px`;
        s.marginLeft = `${rect ? rect.x - frame.x : 0}px`;
        s.marginTop = `${rect ? rect.y - frame.y : 0}px`;
        el.style.scrollPaddingLeft = before ? `${before}px` : '';
        el.style.scrollPaddingRight = after ? `${after}px` : '';
        if (axes !== 'horizontal') {
          extent = { content: (rect ? rect.y - frame.y : 0) + size.height + bottomInset, viewport: frame.height };
        }
      }
      bindScrollUnderBar(el, frame, chrome?.['topInset'], chrome?.['largeTitle']);
      bindScrollEdgeBottom(el, String(node.props['axes'] ?? 'vertical') === 'horizontal' ? undefined : chrome?.['bottomInset'], extent);
      return;
    }
    case 'navscreen': {
      const bar = el.querySelector<HTMLElement>(':scope > .sb-navbar');
      const largeTitle = largeTitleOf(el);
      const inlineTitle = el.querySelector<HTMLElement>(':scope > .sb-navbar > .sb-navbar-title');
      const texts = result.chromeText.get(node.id);
      const barRect = chrome?.['bar'];
      // `.toolbar(.hidden, for: .navigationBar)`: the bar fades out where it
      // was, its title, back button and More button with it (styles.css);
      // `.statusBarHidden`: the frame's drawn status bar fades (renderer.ts).
      const barHidden = node.props['navigationBarHidden'] === true;
      setFlag(el, 'sbBarHidden', barHidden ? '' : null);
      setFlag(el, 'sbStatusHidden', node.props['statusBarHidden'] === true ? '' : null);
      if (bar && barRect) {
        placeChrome(bar, barRect, frame);
        // The 44px bar row sits under the status bar portion of the rect
        // (iOS 26: an inline bar keeps 10 more below it).
        const row = chrome?.['barRow'];
        const top = row ? row.y - barRect.y : Math.max(0, barRect.height - NAV_BAR_HEIGHT);
        const bottom = row ? Math.max(0, barRect.y + barRect.height - (row.y + row.height)) : 0;
        bar.style.paddingTop = `${top}px`;
        bar.style.paddingBottom = `${bottom}px`;
      }
      // The Mac idiom: a unified title bar (styles.css lays its title out leading-aligned).
      setFlag(el, 'sbUnified', chrome?.['unified'] ? '' : null);
      // The Mac idiom: a sheet's title bar (the title leading, 13 bold).
      setFlag(el, 'sbSheetBar', chrome?.['sheetBar'] ? '' : null);
      syncBarGlass(result, node, el, chrome, frame, isNew, changes);
      // The `keyboard` toolbars' bar above the keyboard while a text input of the screen is focused.
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-navscreen-body > .sb-keyboard-bar'), chrome?.['keyboardBar'], frame);
      // iOS 26 in a tab view: the search field under the title (styles.css).
      setFlag(el, 'sbSearchTop', chrome?.['searchTop'] ? '' : null);
      // The Mac idiom: the search field in the title bar (styles.css).
      setFlag(el, 'sbSearchBar', chrome?.['searchBar'] ? '' : null);
      const largeRect = chrome?.['largeTitle'];
      if (largeTitle) {
        if (largeRect) {
          // A title that scrolls with the screen's scroll view is placed by
          // it (src/largeTitle.ts).
          if (largeTitleScrolls(el, largeTitle)) largeTitle.hidden = false;
          else placeChrome(largeTitle, largeRect, frame);
          if (texts?.['largeTitle']) {
            setFont(largeTitle, texts['largeTitle'].font);
            const line = texts['largeTitle'].lines.join('\n');
            const text = largeTitleTextOf(largeTitle);
            if (text.textContent !== line) text.textContent = line;
          }
        } else {
          largeTitle.hidden = true;
        }
      }
      if (barHidden) return;
      // The engine's choice, not the display mode's: a compact height (a
      // phone on its side) has no large title row, and the bar shows the
      // inline title as UIKit does (styles.css hides it under a large one).
      el.dataset['sbDisplay'] = largeRect ? 'large' : 'inline';
      if (inlineTitle) {
        // Phase 6: the engine centers the inline title in the room the back
        // button and the toolbar items leave (no rect: large title or a
        // `principal` toolbar replaces it).
        const titleRect = chrome?.['title'];
        placeChrome(inlineTitle, titleRect, frame);
        if (titleRect) {
          if (texts?.['title']) setFont(inlineTitle, texts['title'].font);
          const lineHeight = `${titleRect.height}px`;
          if (inlineTitle.style.lineHeight !== lineHeight) inlineTitle.style.lineHeight = lineHeight;
        }
      }
      // The Mac idiom: the subtitle under the title in a unified title bar (chrome `subtitle`, hidden otherwise).
      const subtitleEl = el.querySelector<HTMLElement>(':scope > .sb-navbar > .sb-navbar-subtitle');
      if (subtitleEl) {
        const subtitleRect = chrome?.['subtitle'];
        placeChrome(subtitleEl, subtitleRect, frame);
        if (subtitleRect) {
          if (texts?.['subtitle']) setFont(subtitleEl, texts['subtitle'].font);
          const lineHeight = `${subtitleRect.height}px`;
          if (subtitleEl.style.lineHeight !== lineHeight) subtitleEl.style.lineHeight = lineHeight;
        }
      }
      // The More button of the collapsed `secondary` items, at the trailing end.
      const more = el.querySelector<HTMLElement>(':scope > .sb-navbar > .sb-navbar-more');
      placeChrome(more, chrome?.['more'], frame);
      // The overflow menu (`»`) of a Mac sidebar's bar items it could not fit, beside it in the detail's bar.
      if (more) setFlag(more, 'sbOverflow', chrome?.['overflow'] ? '' : null);
      const back = el.querySelector<HTMLElement>(':scope > .sb-navbar > .sb-navbar-back');
      const backLabel = back?.querySelector<HTMLElement>('.sb-navbar-back-label');
      const backRect = chrome?.['back'];
      if (back && backLabel && backRect && texts?.['back']) {
        const text = texts['back'].lines[0] ?? '';
        back.style.maxWidth = `${backRect.width}px`;
        // The bar pads its row 16 (styles.css); the button goes where the
        // engine put it (chrome.back): further in past a split view's
        // sidebar toggle, or less on a Mac, whose bar items start 10 in (so
        // the chevron sits on its capsule, which the engine places there).
        const indent = barRect ? backRect.x - barRect.x - 16 : 0;
        const was = parseFloat(back.style.marginLeft) || 0;
        // A split view's sidebar sliding in or out moves it in with the
        // column (tweened as an offset: it is laid out in the bar's row).
        if (indent !== was && !isNew && chromeChanges) {
          const size = { y: 0, width: backRect.width, height: backRect.height };
          chromeChanges.push({ id: -1, el: back, kind: CHROME_KIND, from: { x: was, ...size }, to: { x: indent, ...size } });
        }
        back.style.marginLeft = indent !== 0 ? `${indent}px` : '';
        backLabel.dataset['sbLayoutLabel'] = text;
        if (backLabel.textContent !== text) backLabel.textContent = text;
        setFont(back, texts['back'].font);
      }
      return;
    }
    case 'sheet': {
      const card = el.querySelector<HTMLElement>(':scope > .sb-sheet-card');
      const grabber = card?.querySelector<HTMLElement>(':scope > .sb-sheet-grabber') ?? null;
      placeChrome(grabber, chrome?.['grabber'], frame);
      // iOS 26: a sheet at a partial detent floats as a rounded card (styles.css).
      setFlag(el, 'sbFloating', chrome?.['floating'] ? '' : null);
      // iPadOS: in a regular width, a centered form sheet (styles.css).
      setFlag(el, 'sbForm', chrome?.['form'] ? '' : null);
      // The Mac idiom: a bottom bar with the sheet's actions (styles.css).
      setFlag(el, 'sbActions', chrome?.['actions'] ? '' : null);
      return;
    }
    case 'tabview': {
      const bar = el.querySelector<HTMLElement>(':scope > .sb-tabbar');
      const barRect = chrome?.['bar'];
      if (!bar || !barRect) return;
      placeChrome(bar, barRect, box);
      // The Mac idiom: the bar is a segmented control at the top of the content (styles.css).
      setFlag(el, 'sbTabsTop', chrome?.['top'] ? '' : null);
      // Items are the 49px row at the top of the bar; the rest is the safe area.
      const items = Array.from(bar.children) as HTMLElement[];
      items.forEach((item, i) => placeChrome(item, chrome?.[`item${i}`], barRect));
      return;
    }
    case 'inspector': {
      setFlag(el, 'sbPresented', node.props['presented'] === true ? '' : null);
      return;
    }
    case 'navsplit': {
      // Phase 7: the sidebar toggle (the sidebar column draws the hairline).
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-navsplit-toggle'), chrome?.['toggle'], box);
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-navsplit-panel'), chrome?.['panel'], box);
      // The Mac's column divider, astride the panel's trailing edge; it
      // carries the sidebar's width for the drag that resizes it (renderer.ts).
      const divider = el.querySelector<HTMLElement>(':scope > .sb-navsplit-divider');
      placeChrome(divider, chrome?.['divider'], box, false);
      if (divider && chrome?.['panel']) divider.dataset['sbWidth'] = String(chrome['panel'].width);
      setFlag(el, 'sbSidebarHidden', node.props['sidebarVisible'] === false ? '' : null);
      return;
    }
    case 'gauge': {
      // The track and fill are chrome, positioned from the element's own box.
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-gauge-track'), chrome?.['track'], box);
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-gauge-fill'), chrome?.['fill'], box);
      // A circular style: the ring over the gauge's square (gaugeRing.ts).
      placeChrome(el.querySelector<HTMLElement>(':scope > .sb-gauge-ring'), chrome?.['ring'], box);
      return;
    }
    case 'chart':
      // Phase 7: the plot SVG, ticks, text labels and legend are rebuilt
      // from the props and the engine's `plot` / label / legend rects.
      applyChartChrome(el, node, result, box);
      return;
    case 'listrow':
      // A selectable or editable row is the list's row: the engine reads a
      // badge through it and puts the badge's rect on it, so it draws it.
      applyBadge(result, node, el, box);
      // Phase 8: the edit-mode control (selection circle / minus) at the
      // engine's `editControl` rect and the reorder grip at `reorderControl`;
      // hidden when the list is not editing.
      placeEditControl(el.querySelector<HTMLElement>(':scope > .sb-listrow-control'), chrome?.['editControl'], box);
      placeEditControl(el.querySelector<HTMLElement>(':scope > .sb-swipe-content > .sb-listrow-grip'), chrome?.['reorderControl'], box);
      return;
    case 'picker': {
      setFont(el, result.fonts.get(node.id));
      if (node.props['style'] === 'segmented') {
        // In a navigation bar item the control is the bar's glass capsule (engine `bar`).
        el.toggleAttribute('data-sb-bar', chrome?.['bar'] !== undefined);
        // The Mac idiom: the label before the control (engine `label`, `track`).
        placeChrome(el.querySelector<HTMLElement>(':scope > .sb-segment-track'), chrome?.['track'] ?? frame, box);
        placeChrome(el.querySelector<HTMLElement>(':scope > .sb-picker-label'), chrome?.['label'], box);
        const segments = el.querySelectorAll<HTMLElement>(':scope > .sb-segment');
        segments.forEach((segment, i) => placeChrome(segment, chrome?.[`segment${i}`], box));
      } else if (node.props['style'] === 'inline') {
        // Phase 6: a label row (when the label is non-empty) and one 44px row per option.
        placeChrome(el.querySelector<HTMLElement>(':scope > .sb-picker-inline-label'), chrome?.['label'], box);
        const options = el.querySelectorAll<HTMLElement>(':scope > .sb-picker-option');
        options.forEach((option, i) => placeChrome(option, chrome?.[`option${i}`], box));
      } else {
        placeChrome(el.querySelector<HTMLElement>(':scope > .sb-picker-label'), chrome?.['label'], box);
        placeChrome(el.querySelector<HTMLElement>(':scope > .sb-picker-value'), chrome?.['value'], box);
        // The Mac idiom: the value in a pop-up button (styles.css).
        setFlag(el, 'sbPopup', chrome?.['popup'] ? '' : null);
      }
      return;
    }
    default:
      return;
  }
}
